'use client'

import { useState, useEffect } from 'react'

/**
 * ObfuscatedEmail Component
 *
 * Displays an email address that's been obfuscated to protect against scrapers.
 * Receives an obfuscated email string from the server and decodes it client-side.
 *
 * Usage (server component):
 *   import { getObfuscatedEmail } from '@/app/_lib/services/EmailObfuscationService'
 *   const obfuscated = getObfuscatedEmail('INFO_EMAIL')
 *   <ObfuscatedEmail obfuscated={obfuscated} />
 *   <ObfuscatedEmail obfuscated={obfuscated}>Contact Us</ObfuscatedEmail>
 *   <ObfuscatedEmail obfuscated={obfuscated} className="text-primary underline">Email Us</ObfuscatedEmail>
 *
 * @param {string} obfuscated - The obfuscated email string from server
 * @param {ReactNode} children - Optional custom link text (defaults to email address)
 * @param {string} className - Optional CSS classes for styling
 */
const ObfuscatedEmail = ({ obfuscated, children, className = '' }) => {
    const [email, setEmail] = useState('')
    const [href, setHref] = useState('#')

    // Reverse the obfuscated string
    const reObfuscateCore = (sTmp) => {
        let i = 0
        let sOutput = ""
        while (i < sTmp.length) {
            sOutput = sTmp.charAt(i) + sOutput
            i = i + 1
        }
        return sOutput
    }

    // Decode HTML entities (&#99; format)
    const unEncodeEmailChars = (sTmp) => {
        let i = 0
        let iNum = 0
        let sNum = ""
        let sOutput = ""
        let chChar = "x"

        while (i < sTmp.length) {
            if (sTmp.charAt(i) == "&") {
                sNum = ""
                i = i + 2  // skip &#

                if (sTmp.charAt(i) != ";") {
                    sNum = sNum + sTmp.charAt(i)
                    i = i + 1
                }
                if (sTmp.charAt(i) != ";") {
                    sNum = sNum + sTmp.charAt(i)
                    i = i + 1
                }
                if (sTmp.charAt(i) != ";") {
                    sNum = sNum + sTmp.charAt(i)
                    i = i + 1
                }
                if (sTmp.charAt(i) == ";") {
                    i = i + 1
                }

                iNum = parseInt(sNum)
                chChar = String.fromCharCode(iNum)
                sOutput = sOutput + chChar
            } else {
                sOutput = sOutput + sTmp.charAt(i)
                i = i + 1
            }
        }
        return sOutput
    }

    useEffect(() => {
        if ( ! obfuscated) {
            return
        }

        // First reverse the string
        const reversed = reObfuscateCore(obfuscated)
        // Then decode HTML entities
        const decoded = unEncodeEmailChars(reversed)

        setEmail(decoded)
        setHref(`mailto:${decoded}`)
    }, [obfuscated])

    if ( ! email) {
        return null
    }

    return (
        <a href={href} className={className}>
            {children || email}
        </a>
    )
}

export default ObfuscatedEmail
